import { useRef } from 'react';
import type { FC } from 'react';
import { ExperienceName } from '@helpcenter/constants';
import { isUiModificationsSupportEnabled } from '@helpcenter/feature-flags';
import { useRouter } from 'react-resource-router';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { addSpanToAll } from '@atlaskit/react-ufo/interaction-metrics';
import getUFORouteName from '@atlaskit/react-ufo/route-name';
import traceUFOPageLoad from '@atlaskit/react-ufo/trace-pageload';
import traceUFOTransition from '@atlaskit/react-ufo/trace-transition';
import { startScreenTTIWithUIModifications } from '@atlassian/jsm-ui-modifications-view-request-create/src/common/utils/performance-analytics/request-create';

const INTERESTING_RESOURCE_PATTERNS = [
    { spanLabel: 'jsapi.js', regEx: /\/jsapi\.js/ },
    { spanLabel: 'challenge.js', regEx: /\/challenge\.js/ },
    { spanLabel: 'vendor-react.chunk.js', regEx: /\/vendor-react\.chunk\..{0,64}js/ },
    { spanLabel: 'servicedesk-vendor.chunk.js', regEx: /\/servicedesk-vendor\.chunk\..{0,64}js/ },
    { spanLabel: 'servicedesk-atlaskit.chunk.js', regEx: /\/servicedesk-atlaskit\.chunk\..{0,64}js/ },
    { spanLabel: 'compiled-css.chunk.js', regEx: /\/compiled-css\.chunk\..{0,64}js/ },
    { spanLabel: 'servicedesk-customerportal.bundle.js', regEx: /\/servicedesk-customerportal\.bundle\..{0,64}js/ },
] as const;

function addResourceTimingsToUFO() {
    if (__SERVER__) {
        return;
    }

    globalThis.performance?.getEntriesByType &&
        Array.from(globalThis.performance?.getEntriesByType('resource')).forEach((entry) => {
            for (const resource of INTERESTING_RESOURCE_PATTERNS) {
                if (resource.regEx.test(entry.name)) {
                    addSpanToAll(
                        'fetch',
                        resource.spanLabel,
                        [{ name: 'custom-network' }, { name: 'resources' }],
                        entry.startTime,
                        entry.startTime + entry.duration
                    );
                }
            }
        });
}

export const UFOWatcher: FC = () => {
    const isInitial = useRef(true);
    const [routerState] = useRouter();
    const route = routerState.route;
    const match = routerState.match;

    let ufoName = getUFORouteName(route).toLowerCase();

    if ('getUFONameBasedOnRouteMatch' in route && route.getUFONameBasedOnRouteMatch instanceof Function) {
        ufoName = route.getUFONameBasedOnRouteMatch(match) as string;
    }

    const lastUFOName = useRef(ufoName);
    if (ufoName) {
        if (isUiModificationsSupportEnabled() && ufoName === ExperienceName.REQUEST_CREATE) {
            startScreenTTIWithUIModifications();
        }
        if (isInitial.current) {
            FeatureGates.checkGate('custom_ufo_spans_for_help_center_bootstrap') && addResourceTimingsToUFO();
            traceUFOPageLoad(ufoName);
            isInitial.current = false;
        }
        if (lastUFOName.current !== ufoName && !isInitial.current) {
            lastUFOName.current = ufoName;
            traceUFOTransition(ufoName);
        }
    }
    return null;
};
